                         import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../api";
import { asArray, isRecord } from "../payloadGuards";

export interface Invitation {
    id: string;
    code: string;
    role: string;
    practice?: {
        id: string;
        name: string;
    };
    email: string;
    status: 'pending' | 'accepted' | 'expired';
    createdAt: string;
    expiresAt: string;
}

export interface InvitationState {
    invitation: Invitation | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
    isLoading: boolean;
    invitations: Invitation[];
}

const initialState: InvitationState = {
    invitation: null,
    status: 'idle',
    error: null,
    isLoading: false,
    invitations: []
};

export const fetchInvitationByCode = createAsyncThunk(
    'invitation/fetchInvitationByCode',
    async (code: string) => {
        try {
            const response = await api.get(`/invitation/${code}`);

            return response.data.body;
        } catch (error) {
            let errorMessage = "Failed to fetch invitation";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

export const createInvitation = createAsyncThunk(
    'invitation/create',
    async (
        invitationData: { id_practice: string; first_name: string; last_name: string; email: string },
        { rejectWithValue }
    ) => {
        try {
            const response = await api.post('/invitation', invitationData);
            // const response = await axios.post('https://hook.us1.make.com/bk0sxfmip4gr8v6at1h4i2yh7pxe4jxq', invitationData);
            return response.data.body;
        } catch (error) {
            // Reject rather than return: returning made failures arrive as `fulfilled`
            // with a string payload, which then overwrote state with that string.
            const errorMessage = error instanceof Error ? error.message : "Failed to create invitation";
            return rejectWithValue(errorMessage);
        }
    }
);

export const fetchInvitations = createAsyncThunk(
    'invitation/fetchInvitations',
    async (_, { rejectWithValue }) => {
        try {
            const practiceId = JSON.parse(localStorage.getItem('user') || '{}')?.["custom:id_practice"];
            const response = await api.get(`/practice/${practiceId}/invitations`);
            return response.data.body;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to fetch invitations";
            return rejectWithValue(errorMessage);
        }
    }
);

export const createSimplePracticeInvitation = createAsyncThunk(
    'invitation/createSimplePracticeInvitation',
    async (payload: { clinicianId: string; email: string; first_name: string; last_name: string; practice_id: string; practice_name: string }) => {
        const response = await api.post('/invitation/simplepractice', payload);
        return response.data.body;
    }
);

export const createSimplePracticeInvitationBulk = createAsyncThunk(
    'invitation/createSimplePracticeInvitationBulk',
    async (payload: { clinicianIds: string[] }) => {
        const response = await api.post('/invitation/simplepractice/bulk', payload);
        return response.data.body;
    }
);

export const updateInvitationStatus = createAsyncThunk(
    'invitation/updateStatus',
    async (payload: { invitationCode: string; status: string; date_clicked?: string; date_completed?: string }) => {
        try {
            const response = await api.patch(`/invitation/${payload.invitationCode}/status`, {
                status: payload.status,
                date_clicked: payload.date_clicked,
                date_completed: payload.date_completed
            });
            return response.data.body || response.data;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to update invitation status";
            throw new Error(errorMessage);
        }
    }
);



const invitationSlice = createSlice({
    name: 'invitation',
    initialState,
    reducers: {
        clearInvitation: (state) => {
            state.invitation = null;
            state.status = 'idle';
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchInvitationByCode.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchInvitationByCode.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.invitation = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchInvitationByCode.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Unknown error occurred';
                state.isLoading = false;
            })
            .addCase(createInvitation.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(createInvitation.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.invitation = isRecord(action.payload)
                    ? (action.payload as unknown as Invitation)
                    : null;
                state.isLoading = false;
            })
            .addCase(createInvitation.rejected, (state, action) => {
                state.status = 'failed';
                state.error = (action.payload as string) || action.error.message || 'Unknown error occurred';
                state.isLoading = false;
            })
            .addCase(fetchInvitations.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchInvitations.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.invitations = asArray<Invitation>(action.payload);
                state.isLoading = false;
            })
            .addCase(fetchInvitations.rejected, (state, action) => {
                state.status = 'failed';
                state.error = (action.payload as string) || action.error.message || 'Unknown error occurred';
                state.isLoading = false;
            })
            .addCase(createSimplePracticeInvitation.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(createSimplePracticeInvitation.fulfilled, (state, action) => {
                state.status = 'succeeded';
                if (isRecord(action.payload)) {
                    state.invitations.push(action.payload as unknown as Invitation);
                }
                state.isLoading = false;
            })
            .addCase(createSimplePracticeInvitation.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Unknown error occurred';
                state.isLoading = false;
            })
            .addCase(createSimplePracticeInvitationBulk.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(createSimplePracticeInvitationBulk.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // The bulk endpoint returns a list; pushing it whole nested an
                // array inside `invitations` instead of appending its members.
                if (Array.isArray(action.payload)) {
                    state.invitations.push(...action.payload.filter(isRecord) as unknown as Invitation[]);
                } else if (isRecord(action.payload)) {
                    state.invitations.push(action.payload as unknown as Invitation);
                }
                state.isLoading = false;
            })
            .addCase(createSimplePracticeInvitationBulk.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Unknown error occurred';
                state.isLoading = false;
            })
            .addCase(updateInvitationStatus.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateInvitationStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                // Update the invitation in state if it matches the current invitation
                if (state.invitation && (state.invitation as any).code === action.payload?.code) {
                    state.invitation = { ...state.invitation, ...action.payload };
                }
            })
            .addCase(updateInvitationStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to update invitation status';
            });
    },
});

export const { clearInvitation } = invitationSlice.actions;
export default invitationSlice.reducer; 
